import React, { Component } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { connect } from 'react-redux';
import { random } from '../../utils/random';
import { addListener, clearListeners } from './alert-service';
import Icon, { COLORS, ICONS } from './icon';
import { clearStorage } from '../../utils/local-storage';
import type { MediaItem, MediaState } from '../../stores/types/media';
import type { SessionState } from '../../stores/types/session';
import * as MediaActions from '../../stores/actions/media';
import { checkFileName } from '../../utils/media';
import { KEY_ENTER, KEY_RETURN } from '../../utils/key-constants';
import Socket from '../../connection/socket';
import { MEDIA_TYPES } from '../../types/constants';
import { ALERT_TYPES } from './alert-types';
import { setSolutionCenterContentState } from '../../stores/actions/user-interface';
import { 
	SolutionCenterArticles,
	SolutionCenterContent,
	SolutionCenterContentTypes,
} from '../../types/solution-center';
import { ShowboardAlertTypes } from '@brandlive1941/showboard';
import { IconCloseCircle } from '@brandlive1941/pixel';

interface OwnProps {
	// props that will be passed to this component
	socket: Socket | null;
}

interface StateProps {
	MediaReducer: MediaState;
	SessionReducer: SessionState;
}

const mapStateToProps = (state: any, ownProps: any) => ({ ...state, ...ownProps });

interface DispatchProps {
	saveMediaItem: Function;
	setSolutionCenterContentState: Function;
}

const mapDispatchToProps = (dispatch: Function) => ({
	saveMediaItem: async (token: string, mediaItem: MediaItem, callback?: Function) => {
		await MediaActions.saveMediaItem(token, mediaItem, undefined).promise;
		if (callback) callback();
	},
	setSolutionCenterContentState: (content: SolutionCenterContent) => {
		dispatch(setSolutionCenterContentState(content));
	},
});

type Props = StateProps & DispatchProps & OwnProps;

class Alerts extends Component<Props> {
	state = {
		animationStates: {},
		message: '',
		type: 'positive',
		showSpinner: false,
		alerts: [] as any[],
		autoWidth: false,
	};

	shouldComponentUpdate(nextProps: any, nextState: any) {
		if (nextState.alerts.length !== this.state.alerts.length) {
			return true;
		}

		const len = nextState.alerts.length;
		for (let i = 0; i < len; i += 1) {
			if (nextState.alerts[i].animationState !== this.state.alerts[i].animationState) {
				return true;
			}

			if (nextState.alerts[i].show !== this.state.alerts[i].show) {
				return true;
			}
		}

		return false;
	}

	componentDidMount() {
		addListener('showAlert', this.show);
		addListener('showAlertLong', this.showLong);
		addListener('hideAlert', this.hide);
		addListener('noConnectionAlert', this.noConnection);
		addListener('showAlertOpentokMuted', this.showOpentokMuted);
		addListener('hideDeviceAlert', this.hideDeviceAlert);
	}

	componentWillUnmount() {
		clearListeners();
	}

	parseType(type: any) {
		if (type) {
			if (typeof type === 'string') {
				switch (type) {
					case 'warning':
					case 'error':
					case 'neutral':
					case 'media-device':
					case 'cam-update':
					case 'mic-update':
					case 'speaker-update':
					case 'scheduled-stream-notification':
					case 'scheduled-stream-schedule-error':
					case 'autoplay-notification':
					case 'duration-notification':
					case 'media-title-error':
					case 'presenter-cap':
					case 'general-notification':
					case 'srt-notification':
					case 'general-error':
					case 'font-alert-replaced':
					case ShowboardAlertTypes.SegmentDurationMismatch:
						return type;
					case 'chat':
						return 'neutral';
					default: {
						console.warn(
							'Invalid alert type, valid types = \'warning\', \'error\', \'neutral\', \'chat\', \'media-device\' or undefined',
						);
						return 'positive';
					}
				}
			} else {
				switch (type) {
					case ALERT_TYPES.WARNING:
						return 'warning';
					case ALERT_TYPES.ERROR:
						return 'error';
					case ALERT_TYPES.NEUTRAL:
						return 'neutral';
					case ALERT_TYPES.POSITIVE:
						return 'positive';
					case ALERT_TYPES.CHAT:
						return 'chat';
					case ALERT_TYPES.MEDIA_DEVICE:
						return 'media-device';
					case ALERT_TYPES.MEDIA_UPLOADED:
						return 'media-uploaded';
					case ALERT_TYPES.MEDIA_TITLE_ERROR:
						return 'media-title-error';
					case ALERT_TYPES.PRESENTER_CAP:
						return 'presenter-cap';
					case ALERT_TYPES.INFO:
						return ALERT_TYPES[type];
					case ALERT_TYPES.GENERAL_ERROR:
						return 'general-error';
					default:
						return 'neutral';
				}
			}
		} else {
			return 'positive';
		}
	}

	trigger = (alert: any, autoWidth: boolean = false) => {
		this.setState(
			{
				alerts: [...this.state.alerts, alert],
				autoWidth,
			},
			() => {
				setTimeout(() => {
					this.setState({
						alerts: this.state.alerts.map((al) => {
							if (al.id === alert.id) {
								/* eslint-disable-next-line no-param-reassign */
								al.animationState = 'show';
							}

							return al;
						}),
					}, this.forceUpdate);
				}, 50);

				if (alert.duration) {
					/* eslint-disable-next-line no-param-reassign */
					alert.startTime = new Date();
					alert.timer = setTimeout(() => {
						this.hide(alert);
					}, alert.duration);
				}
			},
		);

		return alert.id;
	};

	show = ({
		message = '',
		duration = 5000,
		type = 'positive',
		spinner = false,
		description = '',
		requirementsButton = false,
		children = null,
		onClick = null,
		autoWidth = false,
		actions = [],
	}) => {
		const id = random(10);
		const alert = {
			id,
			duration,
			type: this.parseType(type),
			spinner,
			description,
			animationState: 'hide',
			message,
			show: true,
			showButtons: false,
			requirementsButton,
			children,
			onClick,
			editing: false,
			actions,
		};

		return this.trigger(alert, autoWidth);
	};

	showLong = ({
		message = '',
		type = 'positive',
		spinner = false,
		description = null,
		signOut = false,
		children = null,
		onClick = null,
		duration = 1000000,
	}) => {
		const existingMessage = this.state.alerts.find(
			(al) => al.type === 'media-device' && al.show,
		);

		// if this alert is showing, we don't need to show it again
		if (type === 'media-device' && existingMessage) {
			this.setState({
				alerts: this.state.alerts.map((al) => {
					if (al.id === existingMessage.id) {
						return {
							...al,
							message,
							description,
						};
					}
					return al;
				}),
			}, this.forceUpdate);
			return;
		}

		const id = random(10);
		const alert = {
			id,
			type: this.parseType(type),
			spinner,
			description,
			animationState: 'hide',
			message,
			showButtons: false,
			show: true,
			duration,
			signOut,
			children,
			onClick,
		};

		/* eslint-disable-next-line consistent-return */
		return this.trigger(alert);
	};

	showChat = ({
		name = '', message = '', duration = 6000, image = '', user = '',
	}) => {
		const id = random(10);
		const alert = {
			id,
			message: name,
			description: message,
			duration,
			type: 'neutral',
			showButtons: false,
			show: true,
			image,
			chat: true,
			user,
		};

		return this.trigger(alert);
	};

	showOpentokMuted = ({ message = '', description = '' }) => {
		const id = random(10);
		const alert = {
			id,
			message,
			description,
			duration: null,
			type: 'neutral',
			show: true,
			animationState: 'hide',
			opentokMuted: true,
		};

		return this.trigger(alert);
	};

	showNewRecordingAlert = (message: string) => {
		const id = random(10);
		const alert = {
			id,
			message,
			// description: description,
			duration: null,
			type: ALERT_TYPES.MEDIA_UPLOADED,
			show: true,
			animationState: 'hide',
		};

		return this.trigger(alert);
	};

	noConnection = () => {
		const id = 'no connection';

		const alert = {
			id,
			type: 'warning',
			description:
				'Could not get a connection. Please check your internet and try again.',
			animationState: 'hide',
			message: 'Connection Problem',
			show: true,
			duration: 1000,
		};

		if (this.state.alerts.find((al) => al.id === 'no connection')) {
			// do nothing
		}
		return this.trigger(alert);
	};

	showException = () => {
		const id = 'exception';

		const alert = {
			id,
			type: 'error',
			description:
				'We\'re sorry, Brandlive Presenter ran into an unexpected error. An error report has been sent to our engineers.',
			animationState: 'hide',
			message: 'Unexpected Error',
			show: true,
			duration: 6000,
			exception: true,
		};

		if (this.state.alerts.find((al) => al.id === 'exception')) {
			return undefined;
		}
		return this.trigger(alert);
	};

	hide = (alertOriginal: any) => {
		let alert = alertOriginal;
		if (!alert) {
			if (this.state.alerts.length > 0) {
				/* eslint-disable-next-line prefer-destructuring */
				alert = this.state.alerts[0];
			} else {
				return;
			}
		}

		this.setState(
			{
				alerts: this.state.alerts.map((al) => {
					let { animationState } = al;
					if (al.id === alert.id) {
						animationState = 'hide';
					}

					return {
						...al,
						animationState,
					};
				}),
			},
			() => {
				this.forceUpdate();
				setTimeout(() => {
					this.setState({
						alerts: this.state.alerts.map((al) => {
							let { show } = al;
							if (al.id === alert.id) {
								show = false;
							}

							return {
								...al,
								show,
							};
						}),
					}, this.forceUpdate);
				}, 300);

				setTimeout(() => {
					this.purgeHidden();
				}, 10000);
			},
		);
	};

	hideDeviceAlert = () => {
		const deviceWarning = this.state.alerts.find((al) => al.type === 'media-device' && al.show);
		if (deviceWarning) {
			this.hide(deviceWarning);
		}
	};

	purgeHidden = () => {
		this.setState({
			alerts: this.state.alerts.filter((al) => al.show === true),
		}, this.cleanupExceptions);
	};

	cleanupExceptions = () => {
		this.forceUpdate();
		const timeNow = new Date();

		const exception = this.state.alerts.find((al) => (al.duration && ((al.startTime.getTime() + al.duration + 300) < timeNow.getTime()) && (al.show === true || al.animationState === 'show')));
		if (exception) {
			this.hide(exception);
		}
	};

	showDate = () => new Date().toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
	});

	unmuteOpentok = (alert: any) => {
		// Display the mute buttons in the thumbnails now that browser audio is unmuted
		document.documentElement.className = document.documentElement.className.replace('hide-opentok-mute-buttons', '');
		// window.OT.unblockAudio();
		this.hide(alert);
	};

	signOut = async () => {
		await clearStorage();
		const url = new URL(window.location.href);
		// Remove anything extra from the URL like meeting_id, invite, etc. if it existed
		const updatedUrl = `${url.protocol}//${url.host}`;
		if (window.location.href.indexOf('/producer') >= 0) {
			window.location.href = `${updatedUrl}/producer`;
		} else {
			window.location.href = updatedUrl;
		}
	};

	onActionClicked = (alert: any, cancel?: boolean): boolean => {
		const mediaId = parseInt(alert.message, 10);
		const mediaItem = this.props.MediaReducer.media.find((media) => media.id === mediaId);
		const { token } = this.props.SessionReducer;
		const input = (document.getElementById(`${alert.id}-edit-name`) as HTMLInputElement);
		const span = (document.getElementById(`${alert.id}-name`) as HTMLSpanElement);

		if (!input || !span) {
			document.getElementById(alert.id)?.classList.remove('editing');
			return true;
		}

		if (!cancel) {
			if (!checkFileName(input.value, mediaItem?.type === MEDIA_TYPES.HLS)) {
				input.focus();
				return false;
			}

			if (mediaItem && token) {
				mediaItem.custom_name = input.value;
				this.props.saveMediaItem(token, mediaItem, () => {
					this.props.socket?.mediaItemUpdate(mediaItem);
				});
			}

			span.innerText = input.value;
		} else {
			input.value = alert.description;
		}

		document.getElementById(alert.id)?.classList.remove('editing');
		return true;
	};

	renderAlert = (alert: any) => (alert.show ? (
		<React.Fragment key={alert.id}>
			{alert.type !== this.parseType(ALERT_TYPES.MEDIA_UPLOADED) && (
				<div
					key={alert.id}
					onClick={alert.onClick}
					className={classNames('alert', alert.type, alert.animationState, { 'has-children': !!alert.children, clickable: !!alert.onClick })}
				>
					{(alert.type === 'general-error'
						|| alert.type === 'media-title-error'
						|| alert.type === 'media-device'
						|| alert.type === 'presenter-cap'
						|| alert.type === 'scheduled-stream-schedule-error'
						|| alert.type === ShowboardAlertTypes.SegmentDurationMismatch)
						&& <div className="notification-icon notification-error">
							<IconCloseCircle width={24} height={24}/>
						</div>
					}
					{alert.type === 'mic-update'
						&& <div className="notification-icon notification-success">
						<Icon name={ICONS.MICROPHONE_ON} color={COLORS.WHITE} size={24} />
						</div>
					}
					{alert.type === 'cam-update'
						&& <div className="notification-icon notification-success">
							<Icon name={ICONS.CAMERA_ON} color={COLORS.WHITE} size={24} />
						</div>
					}
					{alert.type === 'speaker-update'
						&& <div className="notification-icon notification-success">
							<Icon name={ICONS.SOUND} color={COLORS.WHITE} size={24} />
						</div>
					}
					{alert.type === 'scheduled-stream-notification'
						&& <div className="notification-icon notification-info">
							<Icon name={ICONS.TIMER} color={COLORS.WHITE} size={24} />
						</div>
					}
					{alert.type === 'autoplay-notification'
						&& <div className="notification-icon notification-info">
							<Icon name={ICONS.SHOT_AUTOPLAY_AUTO} color={COLORS.WHITE} size={24} />
						</div>
					}
					{alert.type === 'duration-notification'
						&& <div className="notification-icon notification-error">
							<Icon name={ICONS.CLOCK_ALT} color={COLORS.WHITE} size={24} />
						</div>
					}
					{alert.type === 'font-alert-replaced'
						&& <div className="notification-icon notification-info">
							<Icon name={ICONS.FONT} color={COLORS.WHITE} size={24} />
						</div>
					}
					{(alert.type === 'srt-notification'
					  	|| alert.type === ALERT_TYPES[ALERT_TYPES.INFO]
						|| alert.type === 'general-notification')
						&& <div className="notification-icon notification-info">
							<Icon name={ICONS.INFO} color={COLORS.WHITE} size={24} />
						</div>
					}

					<div className="alert-content">
						<span className="alert-title">{alert.message}</span>

						{(alert.description || alert.spinner) && (
							<div className="alert-description-wrap">
								{alert.description && (
									<ReactMarkdown
										components={{
											a: (props: any) => <a href={props.href} rel="noopener noreferrer" target="_blank">{props.children}</a>,
										}}
									>
										{ alert.description }
									</ReactMarkdown>
								)}
							</div>
						)}

						{alert.signOut && (
							<div className="alert-buttons">
								<button onClick={this.signOut} className="xsmall">
									Sign Out
								</button>
							</div>
						)}

						{alert.opentokMuted ? (
							<div className="alert-buttons">
								<button onClick={() => this.unmuteOpentok(alert)} id="unmute-streams-btn" className="xsmall positive">
									Unmute Streams
								</button>
							</div>
						) : null}

						{alert.type === 'srt-notification' && (
							<div className="alert-buttons">
								<button
									className="button-pillbox dullgray small"
									onClick={() => {
										this.props.setSolutionCenterContentState({
											id: SolutionCenterArticles.FeatureOverviewSRTConnection,
											type: SolutionCenterContentTypes.Article,
										});
									}}
								>
									<Icon name={ICONS.QUESTION_CIRCLE} size={12} color={COLORS.TEXT_GRAY_200} />
									Learn more
								</button>
							</div>
						)}

						{alert.actions && alert.actions.length > 0 && (
							<div className="alert-buttons">
								{alert.actions.map((action: any, index: number) => (
									<button
										key={index}
										className="button-pillbox dullgray small"
										onClick={() => {
											action.onClick();
											this.hide(alert);
										}}
									>
										{action.title}
									</button>
								))}
							</div>
						)}

						{alert.children && (
							<>{alert.children}</>
						)}
					</div>

					{/* Opentok unmute toaster requires user to click unmute button (no X close button) */}
					{!alert.opentokMuted && (
						<div className="close-alert">
							<div
								className="close-click-area"
								onClick={(event: any) => {
									this.hide(alert);
									event.stopPropagation();
								}}
							>
								<Icon name={ICONS.CLOSE} size={16} color={alert.type === 'media-device' ? COLORS.WHITE : COLORS.DARK_GRAY} />
							</div>
						</div>
					)}
				</div>
			)}
			{alert.type === this.parseType(ALERT_TYPES.MEDIA_UPLOADED) && (
				<div
					id={alert.id}
					className={classNames('alert', this.parseType(ALERT_TYPES.MEDIA_UPLOADED), alert.animationState, 'align-items-center')}
					key={alert.id}
					onClick={alert.onClick}
				>
					<div className={classNames('name', 'flex-center', 'width-full', 'overflow-hidden')}>
						<input
							id={`${alert.id}-edit-name`}
							type='text'
							defaultValue={alert.description}
							className={classNames('edit-name', 'white-space-nowrap', 'width-full', 'show-on-edit')}
							onKeyDown={(event: any) => {
								if ((event.keyCode === KEY_RETURN || event.keyCode === KEY_ENTER)
									&& this.onActionClicked(alert)) {
									this.hide(alert);
								}
							}}
						/>
						<span id={`${alert.id}-name`} className={classNames('font-weight-heavy', 'white-space-nowrap', 'hide-on-edit', 'truncate-overflow')}>{alert.description}</span>&nbsp;
						<span className={classNames('font-weight-heavy', 'white-space-nowrap', 'hide-on-edit', 'margin-right-small')}>saved</span>
					</div>
					<div
						id={`${alert.id}-edit-button`}
						className={classNames('edit-button', 'flex-center', 'hide-on-edit', 'font-weight-heavy')}
						onClick={() => {
							const input = (document.getElementById(`${alert.id}-edit-name`) as HTMLInputElement);

							if (input) {
								if (alert.timer) {
									clearTimeout(alert.timer);
								}

								document.getElementById(alert.id)?.classList.add('editing');
								input.focus();
							}
						}}
					>
						<Icon name={ICONS.EDIT} size={14} color={COLORS.PALE_GREEN} />
						<span>Edit</span>
					</div>
					<div className={classNames('close-alert', 'hide-on-edit')}>
						<div
							className="close-click-area"
							onClick={(event: any) => {
								this.hide(alert);
								event.stopPropagation();
							}}
						>
							<Icon name={ICONS.CLOSE} size={16} color={COLORS.MEDIUM_GRAY} />
						</div>
					</div>
					<div
						id={`${alert.id}-edit-button`}
						className={classNames('edit-button', 'flex-center', 'show-on-edit', 'margin-left-small', 'font-weight-heavy')}
						onClick={(event: any) => {
							if (this.onActionClicked(alert)) {
								this.hide(alert);
							}

							event.stopPropagation();
						}}
					>
						<span>Save</span>
					</div>
					<div
						className={classNames('cancel', 'show-on-edit', 'margin-left-small', 'white-space-nowrap', 'font-weight-heavy', 'cursor-pointer')}
						onClick={(event: any) => {
							this.onActionClicked(alert, true);
							this.hide(alert);
							event.stopPropagation();
						}}
					>
						<span>Cancel</span>
					</div>
				</div>
			)}
		</React.Fragment>
	) : null);

	render() {
		return (
			<div className={classNames('alerts-container', {
				'auto-width': this.state.autoWidth,
			})}>
				{this.state.alerts.map((alert) => this.renderAlert(alert))}
			</div>
		);
	}
}

export default connect<StateProps, DispatchProps>(mapStateToProps, mapDispatchToProps)(Alerts);
